import React, { useEffect, useState } from "react";
import { useLanguage } from "../../context/LanguageContext";
import api from "../../lib/api.ts";
import { formatPrice } from "../../utils/functions";
import Pagination from "../common/Pagination";

const ProfilePayment = () => {
  const { translate, language } = useLanguage();
  const isRTL = language === "ar";
  const [paymentData, setPaymentData] = useState([]);
  const authUser = localStorage.getItem("authUser");
  const user = authUser ? JSON.parse(authUser) : null;
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    itemsPerPage: 10,
  });
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchPaymentData = async () => {
      setIsLoading(true);
      try {
        const response = await api.get(
          `/sellers/commissions?page=${pagination.currentPage}&limit=${pagination.itemsPerPage}`
        );

        if (response.data && response.data.data) {
          setPaymentData(response.data.data);
          setPagination({
            currentPage: response.data.pagination?.currentPage || 1,
            totalPages: response.data.pagination?.totalPages || 1,
            totalItems: response.data.pagination?.totalItems || 0,
            itemsPerPage: response.data.pagination?.itemsPerPage || 10,
          });
        }
      } catch (error) {
        console.error("Error fetching payment data:", error);
      } finally {
        setIsLoading(false);
      }
    };
    fetchPaymentData();
  }, [pagination.currentPage]);

  if (isLoading) {
    return (
      <div
        className='bg-[#FFF9F9] rounded-lg shadow-md'
        dir={isRTL ? "rtl" : "ltr"}
        lang={language}
      >
        {/* Header */}
        <div className='bg-[#fcfcfc1f] text-gray-700 font-medium rounded-t-[16px] px-1 py-1'>
          <div className='p-[20px] text-lg rounded-t-[12px] font-semibold text-gray-700 bg-[#F153291F] border-b border-black pb-2'>
            <h2 className='text-base sm:text-lg font-semibold'>
              {translate("sellerPayment.heading")}
            </h2>
          </div>
        </div>
        <div className='px-3 py-6'>
          <div className='animate-pulse'>
            <div className='h-6 bg-gray-200 rounded w-1/3 mb-6'></div>
            <div className='space-y-3'>
              {[1, 2, 3, 4, 5].map((i) => (
                <div key={i} className='h-16 bg-gray-200 rounded'></div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className='bg-[#FFF9F9] rounded-lg shadow-md'
      dir={isRTL ? "rtl" : "ltr"}
      lang={language}
    >
      {/* Header */}
      <div className='bg-[#fcfcfc1f] text-gray-700 font-medium rounded-t-[16px] px-1 py-1'>
        <div className='p-[20px] text-lg rounded-t-[12px] font-semibold text-gray-700 bg-[#F153291F] border-b border-black pb-2'>
          <h2 className='text-base sm:text-lg font-semibold'>
            {translate("sellerPayment.heading")}
          </h2>
        </div>
      </div>

      <div className='px-3 py-6'>
        {/* Mobile Card View */}
        <div className='block lg:hidden space-y-4'>
          {paymentData.map((transaction) => (
            <div
              key={transaction.id}
              className='bg-white rounded-lg shadow-sm border border-gray-200 p-4'
            >
              <div className='space-y-3'>
                {/* Vehicle Info */}
                <div className='flex justify-between items-start'>
                  <div className='flex-1 min-w-0'>
                    <div className='text-sm font-medium text-gray-900 truncate'>
                      {transaction.vehicle?.name || "-"}
                    </div>
                    <div className='text-xs text-gray-500'>
                      {transaction.vehicle?.lotNumber || "-"}
                    </div>
                  </div>
                  <button
                    className={`px-3 py-1 bg-green-600 text-white rounded-md text-xs whitespace-nowrap ${
                      isRTL ? "mr-2" : "ml-2"
                    }`}
                  >
                    {translate("sellerPayment.buttons.release")}
                  </button>
                </div>

                {/* Financial Details */}
                <div className='grid grid-cols-2 gap-3 text-xs'>
                  <div>
                    <div className='text-gray-500'>
                      {translate("sellerPayment.labels.soldPrice")}
                    </div>
                    <div className='font-semibold text-green-600'>
                      {formatPrice(
                        transaction.soldPrice,
                        "font-semibold text-green-600",
                        "12",
                        "#16a34a"
                      )}
                    </div>
                  </div>
                  <div>
                    <div className='text-gray-500'>
                      {translate("sellerPayment.labels.commission")}
                    </div>
                    <div className='font-semibold text-blue-600'>
                      {formatPrice(
                        transaction.commission,
                        "font-semibold text-blue-600",
                        "12",
                        "#2563eb"
                      )}
                    </div>
                  </div>
                  <div>
                    <div className='text-gray-500'>
                      {translate("sellerPayment.labels.vat5")}
                    </div>
                    <div className='font-semibold text-orange-600'>
                      {formatPrice(
                        transaction.vatAmount,
                        "font-semibold text-orange-600",
                        "12",
                        "#ea580c"
                      )}
                    </div>
                  </div>
                  <div>
                    <div className='text-gray-500'>
                      {translate("sellerPayment.labels.withdrawable")}
                    </div>
                    <div className='font-semibold text-gray-900'>
                      {formatPrice(
                        (Number(transaction.soldPrice) || 0) -
                          (Number(transaction.totalCommission) || 0),
                        "font-semibold text-gray-900",
                        "12",
                        "#111827"
                      )}
                    </div>
                  </div>
                </div>

                {/* Sale Date */}
                <div className='text-xs text-gray-500'>
                  {translate("sellerPayment.labels.saleDate")}:{" "}
                  {new Date(transaction.createdAt).toLocaleDateString(
                    language === "ar" ? "ar-AE" : "en-AE",
                    {
                      year: "numeric",
                      month: "short",
                      day: "numeric",
                      hour: "2-digit",
                      minute: "2-digit",
                    }
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Desktop Table View */}
        <div className='hidden lg:block overflow-x-auto bg-white rounded-lg shadow-sm'>
          <table className='min-w-full divide-y divide-gray-200'>
            <thead className='bg-gray-50'>
              <tr>
                <th
                  className={`px-6 py-3 ${
                    isRTL ? "text-right" : "text-left"
                  } text-xs font-medium text-gray-500 uppercase tracking-wider`}
                >
                  {translate("sellerPayment.labels.vehicle")}
                </th>
                <th
                  className={`px-6 py-3 ${
                    isRTL ? "text-right" : "text-left"
                  } text-xs font-medium text-gray-500 uppercase tracking-wider`}
                >
                  {translate("sellerPayment.labels.soldPrice")}
                </th>
                <th
                  className={`px-6 py-3 ${
                    isRTL ? "text-right" : "text-left"
                  } text-xs font-medium text-gray-500 uppercase tracking-wider`}
                >
                  {translate("sellerPayment.labels.commission")}
                </th>
                <th
                  className={`px-6 py-3 ${
                    isRTL ? "text-right" : "text-left"
                  } text-xs font-medium text-gray-500 uppercase tracking-wider`}
                >
                  {translate("sellerPayment.labels.vat5")}
                </th>
                <th
                  className={`px-6 py-3 ${
                    isRTL ? "text-right" : "text-left"
                  } text-xs font-medium text-gray-500 uppercase tracking-wider`}
                >
                  {translate("sellerPayment.labels.payableToKhatalJazeera")}
                </th>
                <th
                  className={`px-6 py-3 ${
                    isRTL ? "text-right" : "text-left"
                  } text-xs font-medium text-gray-500 uppercase tracking-wider`}
                >
                  {translate("sellerPayment.labels.withdrawableAmount")}
                </th>
                <th
                  className={`px-6 py-3 ${
                    isRTL ? "text-right" : "text-left"
                  } text-xs font-medium text-gray-500 uppercase tracking-wider`}
                >
                  {translate("sellerPayment.labels.saleDate")}
                </th>
                <th
                  className={`px-6 py-3 ${
                    isRTL ? "text-right" : "text-left"
                  } text-xs font-medium text-gray-500 uppercase tracking-wider`}
                >
                  {translate("sellerPayment.labels.action")}
                </th>
              </tr>
            </thead>
            <tbody className='bg-white divide-y divide-gray-200'>
              {paymentData.map((transaction) => (
                <tr key={transaction.id} className='hover:bg-gray-50'>
                  <td className='px-6 py-4'>
                    <div>
                      <div className='text-sm font-medium text-gray-900'>
                        {transaction.vehicle?.name || "-"}
                      </div>
                      <div className='text-sm text-gray-500'>
                        {transaction.vehicle?.lotNumber || "-"}
                      </div>
                    </div>
                  </td>
                  <td className='px-6 py-4 text-sm text-gray-900'>
                    {formatPrice(
                      transaction.soldPrice,
                      "font-semibold text-green-600",
                      "12",
                      "#16a34a"
                    )}
                  </td>
                  <td className='px-6 py-4 text-sm text-gray-900'>
                    {formatPrice(
                      transaction.commission,
                      "font-semibold text-blue-600",
                      "12",
                      "#2563eb"
                    )}
                  </td>
                  <td className='px-6 py-4 text-sm text-gray-900'>
                    {formatPrice(
                      transaction.vatAmount,
                      "font-semibold text-orange-600",
                      "12",
                      "#ea580c"
                    )}
                  </td>
                  <td className='px-6 py-4 text-sm text-gray-900'>
                    {formatPrice(
                      transaction.totalCommission,
                      "font-semibold text-red-600",
                      "12",
                      "#dc2626"
                    )}
                  </td>
                  <td className='px-6 py-4 text-sm text-gray-900'>
                    {formatPrice(
                      (Number(transaction.soldPrice) || 0) -
                        (Number(transaction.totalCommission) || 0),
                      "font-semibold text-gray-900",
                      "12",
                      "#111827"
                    )}
                  </td>
                  <td className='px-6 py-4 whitespace-nowrap text-sm text-gray-500'>
                    {new Date(transaction.createdAt).toLocaleDateString(
                      language === "ar" ? "ar-AE" : "en-AE",
                      {
                        year: "numeric",
                        month: "short",
                        day: "numeric",
                        hour: "2-digit",
                        minute: "2-digit",
                      }
                    )}
                  </td>
                  <td className='px-6 py-4 text-sm text-gray-900'>
                    <button className='px-4 py-2 bg-green-600 text-white rounded-md text-sm whitespace-nowrap hover:bg-green-700 transition-colors'>
                      {translate("sellerPayment.buttons.release")}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Empty State */}
        {paymentData.length === 0 && !isLoading && (
          <div className='text-center py-12'>
            <div className='text-gray-500'>
              <svg
                className='mx-auto h-12 w-12 text-gray-400'
                fill='none'
                viewBox='0 0 24 24'
                stroke='currentColor'
              >
                <path
                  strokeLinecap='round'
                  strokeLinejoin='round'
                  strokeWidth={2}
                  d='M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2'
                />
              </svg>
              <h3 className='mt-2 text-sm font-medium text-gray-900'>
                {translate("sellerPayment.messages.noPaymentsFound")}
              </h3>
              <p className='mt-1 text-sm text-gray-500'>
                {translate("sellerPayment.messages.noPaymentsAvailable")}
              </p>
            </div>
          </div>
        )}

        {/* Pagination */}
        {pagination.totalPages > 1 && (
          <div className='px-3 lg:px-6 py-4 border-t border-gray-200'>
            <Pagination
              totalPages={pagination.totalPages}
              currentPage={pagination.currentPage}
              setCurrentPage={(page) => {
                // You can implement page change logic here
                setPagination({
                  ...pagination,
                  currentPage: page,
                });
                console.log("Page changed to:", page);
              }}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default ProfilePayment;
